/**
 * secureIDB — tiny encrypted IndexedDB key-value store.
 *
 * Threat model
 * ------------
 * localStorage is plaintext and any XSS payload can read it in one line.
 * IndexedDB values *look* like structured data in a devtools panel, but
 * are just as readable from JS. So we go one step further: everything
 * written here is AES-GCM encrypted with a **non-extractable** CryptoKey
 * that lives inside IndexedDB itself.
 *
 * That means:
 *   • A stolen browser profile / IDB backup (attacker has no origin
 *     scripting) is useless — the key can't be extracted from the file.
 *   • Passive XSS scrapers that just dump storage still get gibberish;
 *     they'd have to also invoke `crypto.subtle.decrypt` with the exact
 *     same key handle, which requires more targeted attacker code.
 *   • It's not a defense against active same-origin script that fully
 *     drives the crypto API — that's what CSP + trusted-types are for.
 *     But this hardens the "smash-and-grab" scenario the code review
 *     called out.
 *
 * API — all functions are async:
 *   secureGet(key)      → Promise<any | null>
 *   secureSet(key, val) → Promise<void>
 *   secureRemove(key)   → Promise<void>
 *   secureClear()       → Promise<void>
 *
 * Values are JSON-serialised before encryption. `null` on read means
 * "no such key" (or the value was corrupted / undecryptable).
 */

const DB_NAME = 'maidly_secure';
const DB_VERSION = 1;
const KEYS_STORE = 'keys';
const DATA_STORE = 'data';
const KEY_ID = 'aes_gcm_v1';

let _dbPromise = null;
let _keyPromise = null;

function _openDB() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable in this environment'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(KEYS_STORE)) db.createObjectStore(KEYS_STORE);
      if (!db.objectStoreNames.contains(DATA_STORE)) db.createObjectStore(DATA_STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

function _tx(db, store, mode) {
  return db.transaction(store, mode).objectStore(store);
}

function _idbRequest(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function _getOrCreateKey() {
  if (_keyPromise) return _keyPromise;
  _keyPromise = (async () => {
    const db = await _openDB();
    const existing = await _idbRequest(_tx(db, KEYS_STORE, 'readonly').get(KEY_ID));
    if (existing) return existing;
    // Generate a fresh 256-bit AES-GCM key. `extractable: false` so no
    // JS on this origin can ever call `crypto.subtle.exportKey` on it.
    const key = await crypto.subtle.generateKey(
      { name: 'AES-GCM', length: 256 },
      false, // extractable
      ['encrypt', 'decrypt'],
    );
    // Store the *CryptoKey* object itself (IDB natively serialises it
    // via structured clone — the raw bytes never touch JS memory).
    await _idbRequest(_tx(db, KEYS_STORE, 'readwrite').put(key, KEY_ID));
    return key;
  })();
  return _keyPromise;
}

async function _encrypt(plaintext) {
  const key = await _getOrCreateKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(JSON.stringify(plaintext));
  const cipher = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded);
  return { iv: Array.from(iv), cipher: Array.from(new Uint8Array(cipher)) };
}

async function _decrypt(record) {
  if (!record || !record.iv || !record.cipher) return null;
  try {
    const key = await _getOrCreateKey();
    const iv = new Uint8Array(record.iv);
    const cipher = new Uint8Array(record.cipher);
    const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, cipher);
    return JSON.parse(new TextDecoder().decode(plain));
  } catch (err) {
    console.warn('[secureIDB] decrypt failed for stored entry', err);
    return null;
  }
}

export async function secureGet(key) {
  try {
    const db = await _openDB();
    const record = await _idbRequest(_tx(db, DATA_STORE, 'readonly').get(key));
    return record ? _decrypt(record) : null;
  } catch (err) {
    console.warn('[secureIDB] get failed', key, err);
    return null;
  }
}

export async function secureSet(key, value) {
  try {
    const db = await _openDB();
    const record = await _encrypt(value);
    await _idbRequest(_tx(db, DATA_STORE, 'readwrite').put(record, key));
  } catch (err) {
    console.warn('[secureIDB] set failed', key, err);
  }
}

export async function secureRemove(key) {
  try {
    const db = await _openDB();
    await _idbRequest(_tx(db, DATA_STORE, 'readwrite').delete(key));
  } catch (err) {
    console.warn('[secureIDB] remove failed', key, err);
  }
}

export async function secureClear() {
  try {
    const db = await _openDB();
    await _idbRequest(_tx(db, DATA_STORE, 'readwrite').clear());
  } catch (err) {
    console.warn('[secureIDB] clear failed', err);
  }
}

/**
 * One-shot helper: copy legacy plaintext localStorage values into the
 * encrypted store and delete the plaintext originals. Safe to call on
 * every module load — it's a no-op once the localStorage keys are gone.
 */
export async function migrateFromLocalStorage(keys) {
  if (!keys?.length || typeof localStorage === 'undefined') return;
  for (const key of keys) {
    let raw;
    try {
      raw = localStorage.getItem(key);
    } catch (_) {
      raw = null;
    }
    if (raw === null) continue;
    // Try JSON first; fall back to plain string.
    let parsed = raw;
    try { parsed = JSON.parse(raw); } catch (_) { /* keep as string */ }
    await secureSet(key, parsed);
    try { localStorage.removeItem(key); } catch (_) { /* private mode */ }
  }
}
